import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';

// Types
export interface SubItem {
  key: string;
  icon: string;
  label: string;
  path: string;
  locked: boolean;
  requires_subscription: boolean;
  requires_achievement: boolean;
  feature: string | null;
  feature_data: {
    description?: string;
    achievement_key?: string;
    achievement_name?: string;
    achievement_description?: string;
  };
  method?: 'delete';
}

export interface NavigationItem {
  section: string;
  icon: string;
  label: string;
  path: string;
  active: boolean;
  sub_items: SubItem[];
  method?: 'delete';
}

export interface MainNavigationContainerProps {
  navigationItems: NavigationItem[];
  currentSection: string;
  logoPath: string;
  diaryPath: string;
  translations: {
    mainNavigation: string;
    subscriberFeature: string;
    unlockByAchieving: string;
  };
}

// Desktop Sidebar Navigation Component
function DesktopSidebar({
  navigationItems,
  currentSection,
  logoPath,
  diaryPath,
  translations,
  onLockedFeatureClick,
}: MainNavigationContainerProps & {
  onLockedFeatureClick: (item: SubItem) => void;
}) {
  const isSubItemActive = (section: string, subKey: string): boolean => {
    if (currentSection !== section) return false;

    const path = window.location.pathname;
    switch (subKey) {
      case 'agenda': return path.includes('/diary_days/agenda');
      case 'calendar': return path.includes('/diary_days/calendar');
      case 'stats': return path.includes('/stats');
      case 'reports': return path.includes('/reports');
      case 'goals': return path.includes('/goals');
      case 'achievements': return path.includes('/achievements');
      default: return false;
    }
  };

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: NavigationItem | SubItem,
    isLocked?: boolean
  ) => {
    if (isLocked && 'feature_data' in item) {
      e.preventDefault();
      onLockedFeatureClick(item as SubItem);
      return;
    }

    if ('method' in item && item.method === 'delete') {
      e.preventDefault();
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = item.path;
      form.innerHTML = `
        <input type="hidden" name="_method" value="delete" />
        <input type="hidden" name="authenticity_token" value="${csrfToken}" />
      `;
      document.body.appendChild(form);
      form.submit();
    }
  };

  return (
    <aside
      className="desktop-sidebar-nav"
      role="navigation"
      aria-label={translations.mainNavigation}
      data-test-id="desktop_sidebar_navigation"
    >
      <div className="desktop-sidebar-nav__header">
        <a href={diaryPath} className="desktop-sidebar-nav__logo">
          <img
            src={logoPath}
            style={{ height: '30px' }}
            alt="Traquility"
            className="desktop-sidebar-nav__logo-img"
          />
          <span className="desktop-sidebar-nav__logo-text">TRAQUILITY</span>
        </a>
      </div>

      <nav className="desktop-sidebar-nav__content">
        {navigationItems.map((item) => (
          <div key={item.section} className="desktop-sidebar-nav__section">
            <a
              href={item.path}
              className={`desktop-sidebar-nav__item ${item.active ? 'desktop-sidebar-nav__item--active' : ''}`}
              data-section={item.section}
              aria-current={item.active ? 'page' : undefined}
              data-test-id={`sidebar_item_${item.section}`}
              onClick={(e) => handleLinkClick(e, item)}
              {...(item.method === 'delete' ? { 'data-turbo-method': 'delete' } : {})}
            >
              <i className={`${item.icon} desktop-sidebar-nav__icon`} />
              <span className="desktop-sidebar-nav__label">{item.label}</span>
            </a>

            {item.sub_items.length > 0 && (
              <div className="desktop-sidebar-nav__subitems">
                {item.sub_items.map((subItem) => (
                  <a
                    key={subItem.key}
                    href={subItem.locked ? '#' : subItem.path}
                    className={`desktop-sidebar-nav__subitem ${
                      isSubItemActive(item.section, subItem.key)
                        ? 'desktop-sidebar-nav__subitem--active'
                        : ''
                    }`}
                    data-section={item.section}
                    data-subsection={subItem.key}
                    data-locked={subItem.locked ? 'true' : 'false'}
                    data-test-id={`sidebar_subitem_${subItem.key}`}
                    onClick={(e) => handleLinkClick(e, subItem, subItem.locked)}
                    {...(subItem.method === 'delete' && !subItem.locked
                      ? { 'data-turbo-method': 'delete' }
                      : {})}
                  >
                    <i className={`${subItem.icon} desktop-sidebar-nav__subitem-icon`} />
                    <span className="desktop-sidebar-nav__subitem-label">{subItem.label}</span>
                    {subItem.requires_subscription && (
                      <i
                        className="bi bi-star-fill desktop-sidebar-nav__subscriber-badge"
                        title={translations.subscriberFeature}
                      />
                    )}
                    {subItem.requires_achievement && !subItem.requires_subscription && (
                      <i
                        className="bi bi-lock-fill desktop-sidebar-nav__lock-icon"
                        title={translations.unlockByAchieving}
                      />
                    )}
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
}

// Mobile Submenu Component
function MobileSubmenu({
  item,
  isExpanded,
  submenuRef,
  onLockedFeatureClick,
  translations,
}: {
  item: NavigationItem;
  isExpanded: boolean;
  submenuRef: React.RefObject<HTMLDivElement | null>;
  onLockedFeatureClick: (subItem: SubItem) => void;
  translations: MainNavigationContainerProps['translations'];
}) {
  const handleSubItemClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    subItem: SubItem
  ) => {
    if (subItem.locked) {
      e.preventDefault();
      onLockedFeatureClick(subItem);
      return;
    }

    if (subItem.method === 'delete') {
      e.preventDefault();
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = subItem.path;
      form.innerHTML = `
        <input type="hidden" name="_method" value="delete" />
        <input type="hidden" name="authenticity_token" value="${csrfToken}" />
      `;
      document.body.appendChild(form);
      form.submit();
    }
  };

  return (
    <div
      ref={submenuRef}
      className={`mobile-bottom-nav__submenu ${isExpanded ? 'mobile-bottom-nav__submenu--expanded' : ''}`}
      role="menu"
    >
      {item.sub_items.map((subItem) => (
        <a
          key={subItem.key}
          href={subItem.locked ? '#' : subItem.path}
          className="mobile-bottom-nav__submenu-item"
          data-section={item.section}
          data-subsection={subItem.key}
          role="menuitem"
          data-test-id={`nav_submenu_${subItem.key}`}
          onClick={(e) => handleSubItemClick(e, subItem)}
          {...(subItem.method === 'delete' && !subItem.locked
            ? { 'data-turbo-method': 'delete' }
            : {})}
        >
          <i className={`${subItem.icon} mobile-bottom-nav__submenu-icon`} />
          <span className="mobile-bottom-nav__submenu-label">{subItem.label}</span>
          {subItem.requires_subscription && (
            <i
              className="bi bi-star-fill mobile-bottom-nav__subscriber-badge"
              title={translations.subscriberFeature}
            />
          )}
          {subItem.requires_achievement && !subItem.requires_subscription && (
            <i
              className="bi bi-lock-fill mobile-bottom-nav__lock-icon"
              title={translations.unlockByAchieving}
            />
          )}
        </a>
      ))}
    </div>
  );
}

// Mobile Bottom Navigation Component
function MobileBottomNav({
  navigationItems,
  translations,
  onLockedFeatureClick,
}: MainNavigationContainerProps & {
  onLockedFeatureClick: (item: SubItem) => void;
}) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const submenuRefs = useRef<Record<string, React.RefObject<HTMLDivElement | null>>>({});
  const buttonRefs = useRef<Record<string, React.RefObject<HTMLButtonElement | null>>>({});

  // Initialize refs for each expandable section
  navigationItems.forEach((item) => {
    if (['profile', 'progress'].includes(item.section)) {
      if (!submenuRefs.current[item.section]) {
        submenuRefs.current[item.section] = React.createRef();
      }
      if (!buttonRefs.current[item.section]) {
        buttonRefs.current[item.section] = React.createRef();
      }
    }
  });

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!expandedSection) return;

      const submenuRef = submenuRefs.current[expandedSection];
      const buttonRef = buttonRefs.current[expandedSection];

      if (
        submenuRef?.current &&
        !submenuRef.current.contains(event.target as Node) &&
        buttonRef?.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setExpandedSection(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [expandedSection]);

  // Adjust submenu position when expanded
  useEffect(() => {
    if (!expandedSection) return;

    const submenuRef = submenuRefs.current[expandedSection];
    const buttonRef = buttonRefs.current[expandedSection];

    if (!submenuRef?.current || !buttonRef?.current) return;

    const submenu = submenuRef.current;
    const mainItem = buttonRef.current;
    const viewportWidth = window.innerWidth;

    // Reset any previous adjustments
    submenu.style.left = '';
    submenu.style.setProperty('--triangle-offset', '0px');

    requestAnimationFrame(() => {
      const rect = submenu.getBoundingClientRect();
      const mainItemRect = mainItem.getBoundingClientRect();

      const mainItemCenter = mainItemRect.left + mainItemRect.width / 2;
      const submenuCenterOriginal = rect.left + rect.width / 2;

      let submenuAdjustment = 0;

      if (rect.left < 0) {
        submenuAdjustment = Math.abs(rect.left) + 8;
        submenu.style.left = `calc(50% + ${submenuAdjustment}px)`;
      } else if (rect.right > viewportWidth) {
        submenuAdjustment = -(rect.right - viewportWidth + 8);
        submenu.style.left = `calc(50% + ${submenuAdjustment}px)`;
      }

      const submenuCenterAfterAdjustment = submenuCenterOriginal + submenuAdjustment;
      const triangleOffset = mainItemCenter - submenuCenterAfterAdjustment;
      submenu.style.setProperty('--triangle-offset', `${triangleOffset}px`);
    });
  }, [expandedSection]);

  const handleToggle = (section: string) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: NavigationItem
  ) => {
    if (item.method === 'delete') {
      e.preventDefault();
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = item.path;
      form.innerHTML = `
        <input type="hidden" name="_method" value="delete" />
        <input type="hidden" name="authenticity_token" value="${csrfToken}" />
      `;
      document.body.appendChild(form);
      form.submit();
    }
  };

  return (
    <nav
      className="mobile-bottom-nav"
      role="navigation"
      aria-label={translations.mainNavigation}
      data-test-id="mobile_bottom_navigation"
    >
      <div className="mobile-bottom-nav__container">
        {navigationItems.map((item) => {
          const isExpandable =
            ['profile', 'progress'].includes(item.section) && item.sub_items.length > 0;
          const isExpanded = expandedSection === item.section;

          if (isExpandable) {
            return (
              <div key={item.section} className="mobile-bottom-nav__item-wrapper">
                <button
                  type="button"
                  ref={buttonRefs.current[item.section] as React.RefObject<HTMLButtonElement>}
                  className={`mobile-bottom-nav__item ${item.active ? 'mobile-bottom-nav__item--active' : ''} ${
                    isExpanded ? 'mobile-bottom-nav__item--expanded' : ''
                  } ${isExpanded && !item.active ? 'mobile-bottom-nav__item--expanded-not-active' : ''}`}
                  data-section={item.section}
                  aria-expanded={isExpanded}
                  aria-haspopup="true"
                  data-test-id={`nav_tab_${item.section}`}
                  onClick={() => handleToggle(item.section)}
                >
                  <i className={`${item.icon} mobile-bottom-nav__icon`} />
                  <span className="mobile-bottom-nav__label">{item.label}</span>
                </button>

                <MobileSubmenu
                  item={item}
                  isExpanded={isExpanded}
                  submenuRef={submenuRefs.current[item.section]}
                  onLockedFeatureClick={onLockedFeatureClick}
                  translations={translations}
                />
              </div>
            );
          }

          return (
            <a
              key={item.section}
              href={item.path}
              className={`mobile-bottom-nav__item ${item.active ? 'mobile-bottom-nav__item--active' : ''}`}
              data-section={item.section}
              aria-current={item.active ? 'page' : undefined}
              data-test-id={`nav_tab_${item.section}`}
              onClick={(e) => handleLinkClick(e, item)}
              {...(item.method === 'delete' ? { 'data-turbo-method': 'delete' } : {})}
            >
              <i className={`${item.icon} mobile-bottom-nav__icon`} />
              <span className="mobile-bottom-nav__label">{item.label}</span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}

// Feature Lock Modal Handler
function useFeatureLockModal() {
  const handleShowFeatureLockModal = useCallback((subItem: SubItem) => {
    // Use the global showFeatureLockModal function from FeatureLockModalContainer
    const showModal = (window as any).showFeatureLockModal;
    if (!showModal) return;

    const lockType = subItem.requires_subscription ? 'subscription' : 'achievement';
    const achievementUrl = subItem.feature_data.achievement_key
      ? `/diary/achievements#${subItem.feature_data.achievement_key}`
      : undefined;

    showModal({
      featureName: subItem.label,
      featureDescription: subItem.feature_data.description || '',
      featureIcon: lockType === 'achievement' ? 'trophy' : 'lock-fill',
      lockType,
      requirementText: subItem.requires_achievement ? subItem.feature_data.achievement_description : undefined,
      achievementUrl,
    });
  }, []);

  return { showFeatureLockModal: handleShowFeatureLockModal };
}

// Main Navigation Container Component
export function MainNavigationContainer(props: MainNavigationContainerProps) {
  const { showFeatureLockModal } = useFeatureLockModal();

  return (
    <div className="responsive-navigation">
      <DesktopSidebar {...props} onLockedFeatureClick={showFeatureLockModal} />
      <MobileBottomNav {...props} onLockedFeatureClick={showFeatureLockModal} />
    </div>
  );
}

/**
 * Mount function for rendering from Rails
 */
export function mountMainNavigation(
  container: HTMLElement,
  props: MainNavigationContainerProps
) {
  const root = createRoot(container);
  root.render(<MainNavigationContainer {...props} />);
  return root;
}

export default MainNavigationContainer;
